import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Card,
  FormLayout,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Checkbox,
  Banner,
  RangeSlider,
  ButtonGroup,
  Select,
} from '@shopify/polaris';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../../providers/ApiProvider';
import {
  DEFAULT_PROMO_DESIGN,
  toColorInputValue,
  type PromoDesign,
  type PromoFontWeight,
} from './promoDefaults';
import { PromoPreview } from './PromoPreview';
import {
  ContextualSaveBar,
  SavingOverlay,
  isShopifyEmbedded,
  showAppToast,
  useUnsavedChanges,
} from '../../components/ContextualSaveBar';
import { ThemeColorRefresh } from '../../components/ThemeColorRefresh';
import { ProductBlockStatusBanner } from '../../components/ProductBlockStatusBanner';
import { t } from '../../i18n';

const FONT_OPTS: Array<{ label: string; value: PromoFontWeight }> = [
  { label: 'Regular', value: 'regular' },
  { label: 'Medium', value: 'medium' },
  { label: 'Semibold', value: 'semibold' },
  { label: 'Bold', value: 'bold' },
];

function ColorField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const pickerValue = toColorInputValue(value, '#000000');
  return (
    <div>
      <Text as="p" variant="bodyMd">
        {label}
      </Text>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginTop: 6,
        }}
      >
        <input
          type="color"
          value={pickerValue}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          style={{
            width: 40,
            height: 36,
            padding: 0,
            border: '1px solid #c9cccf',
            borderRadius: 8,
            background: 'transparent',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        />
        <div style={{ flex: 1 }}>
          <input
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: '100%',
              height: 36,
              border: '1px solid #c9cccf',
              borderRadius: 8,
              padding: '0 10px',
              fontSize: 13,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function PromoEditorPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return') || '/widgets';

  const [design, setDesign] = useState<PromoDesign>({ ...DEFAULT_PROMO_DESIGN });
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const embedded = isShopifyEmbedded();
  const { dirty, markClean, getBaseline, markCleanAfterDiscard } =
    useUnsavedChanges(design);

  const patch = useCallback(
    <K extends keyof PromoDesign>(key: K, value: PromoDesign[K]) => {
      setDesign((d) => ({ ...d, [key]: value }));
    },
    [],
  );

  useEffect(() => {
    setLoading(true);
    api
      .get<
        Array<{
          id: string;
          type: string;
          content?: Record<string, unknown>;
          styles?: Record<string, unknown>;
        }>
      >('/widgets')
      .then((list) => {
        const w = (list || []).find((x) => x.type === 'PRODUCT_WIDGET');
        if (!w) {
          const defaults = { ...DEFAULT_PROMO_DESIGN };
          setDesign(defaults);
          markClean(defaults);
          return;
        }
        setWidgetId(w.id);
        const s = w.styles || {};
        const weight = (v: unknown, fallback: PromoFontWeight): PromoFontWeight => {
          const x = String(v || '');
          if (x === 'regular' || x === 'medium' || x === 'semibold' || x === 'bold') {
            return x;
          }
          return fallback;
        };
        const next: PromoDesign = {
          ...DEFAULT_PROMO_DESIGN,
          titleFontWeight: weight(s.titleFontWeight, DEFAULT_PROMO_DESIGN.titleFontWeight),
          subtitleFontWeight: weight(
            s.subtitleFontWeight,
            DEFAULT_PROMO_DESIGN.subtitleFontWeight,
          ),
          accentColor: String(s.accentColor ?? DEFAULT_PROMO_DESIGN.accentColor),
          backgroundColor: String(
            s.backgroundColor ?? DEFAULT_PROMO_DESIGN.backgroundColor,
          ),
          textColor: String(s.textColor ?? DEFAULT_PROMO_DESIGN.textColor),
          mutedColor: String(s.mutedColor ?? DEFAULT_PROMO_DESIGN.mutedColor),
          borderColor: String(s.borderColor ?? DEFAULT_PROMO_DESIGN.borderColor),
          giftCardBackground: String(
            s.giftCardBackground ?? DEFAULT_PROMO_DESIGN.giftCardBackground,
          ),
          giftCardBorder: String(
            s.giftCardBorder ?? DEFAULT_PROMO_DESIGN.giftCardBorder,
          ),
          comparePriceColor: String(
            s.comparePriceColor ?? DEFAULT_PROMO_DESIGN.comparePriceColor,
          ),
          borderRadius: Number(s.borderRadius ?? DEFAULT_PROMO_DESIGN.borderRadius),
          fontFamily: String(s.fontFamily ?? DEFAULT_PROMO_DESIGN.fontFamily),
          autoFitTheme: s.autoFitTheme !== false,
          themeSchemeId:
            typeof s.themeSchemeId === 'string' ? s.themeSchemeId : null,
        };
        setDesign(next);
        markClean(next);
      })
      .catch(() => {
        const defaults = { ...DEFAULT_PROMO_DESIGN };
        setDesign(defaults);
        markClean(defaults);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  async function save() {
    setSaving(true);
    setError(null);
    setMessage(null);
    const snap = { ...design };
    try {
      const payload = {
        name: 'Product Page Promotion Widget',
        type: 'PRODUCT_WIDGET' as const,
        placement: 'PRODUCT' as const,
        content: {},
        styles: {
          titleFontWeight: snap.titleFontWeight,
          subtitleFontWeight: snap.subtitleFontWeight,
          accentColor: snap.accentColor,
          backgroundColor: snap.backgroundColor,
          textColor: snap.textColor,
          mutedColor: snap.mutedColor,
          borderColor: snap.borderColor,
          giftCardBackground: snap.giftCardBackground,
          giftCardBorder: snap.giftCardBorder,
          comparePriceColor: snap.comparePriceColor,
          borderRadius: snap.borderRadius,
          fontFamily: snap.fontFamily,
          autoFitTheme: snap.autoFitTheme,
          themeSchemeId: snap.themeSchemeId,
        },
        layout: { position: 'custom', fullWidth: false },
        animations: { enter: 'fade', durationMs: 200 },
        isEnabled: true,
      };

      if (widgetId) {
        await api.patch(`/widgets/${widgetId}`, payload);
      } else {
        const created = await api.post<{ id: string }>('/widgets', payload);
        setWidgetId(created.id);
      }
      await api
        .patch('/stores/setup', { widgetCustomized: true })
        .catch(() => undefined);
      markClean(snap);
      setMessage(
        t("Product Page Promotion Widget saved. Add the product block on the PDP, then enable it on a campaign."),
      );
      showAppToast(t("Product Page Promotion Widget saved"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      showAppToast(msg, { isError: true });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    const base = getBaseline();
    if (!base) return;
    setDesign(base);
    setError(null);
    setMessage(null);
    markCleanAfterDiscard(base);
  }

  return (
    <Page
      title={t("Edit Product Page Promotion Widget")}
      subtitle={t("Product block · Spend / Add X / BXGY gift carousel")}
      backAction={{
        content: t("Back"),
        onAction: () => navigate(returnTo),
      }}
      primaryAction={
        !embedded
          ? {
              content: t("Save widget"),
              loading: saving,
              disabled: !dirty,
              onAction: () => void save(),
            }
          : undefined
      }
      secondaryActions={[
        {
          content: t("Reset defaults"),
          onAction: () => setDesign({ ...DEFAULT_PROMO_DESIGN }),
        },
      ]}
    >
      <ContextualSaveBar
        id="freebi-promo-save"
        open={embedded && dirty && !loading}
        saving={saving}
        onSave={save}
        onDiscard={discard}
        saveLabel="Save"
      />
      <SavingOverlay open={saving} label={t("Saving promotion widget…")} />
      <BlockStack gap="400">
        {message && (
          <Banner tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </Banner>
        )}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {loading && (
          <Banner tone="info">
            <p>{t("Loading Product Page Promotion Widget…")}</p>
          </Banner>
        )}

        <Banner tone="info">
          <p>
            {t("Title and subtitle are set per campaign under Display settings. This\n            page only controls fonts and colors. The widget appears on the product\n            page only after you add the")} <strong>{t("Product promotion")}</strong> {t("block\n            and enable it on a Spend / Add X / BXGY campaign. Existing campaigns\n            stay off until you turn it on.")}
          </p>
        </Banner>

        <ProductBlockStatusBanner
          block="product-promo"
          blockName="Freebi Product promotion"
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 380px) minmax(0, 1fr)',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t("Typography")}
              </Text>
              <FormLayout>
                <Select
                  label={t("Title font")}
                  options={FONT_OPTS}
                  value={design.titleFontWeight}
                  onChange={(v) =>
                    patch('titleFontWeight', v as PromoFontWeight)
                  }
                />
                <Select
                  label={t("Subtitle font")}
                  options={FONT_OPTS}
                  value={design.subtitleFontWeight}
                  onChange={(v) =>
                    patch('subtitleFontWeight', v as PromoFontWeight)
                  }
                />
              </FormLayout>

              <Text as="h2" variant="headingMd">
                {t("Auto-fit with your theme")}
              </Text>
              <Banner tone="info">
                <p>
                  {t("When enabled, the storefront samples colors from the live theme\n                  so the widget matches your brand.")}
                </p>
              </Banner>
              <Checkbox
                label={t("Auto-fit with your theme")}
                checked={design.autoFitTheme}
                onChange={(v) => patch('autoFitTheme', v)}
              />
              <ThemeColorRefresh
                onApply={(c) => {
                  setDesign((d) => ({
                    ...d,
                    autoFitTheme: true,
                    themeSchemeId: c.schemeId || d.themeSchemeId,
                    accentColor: c.accentColor || d.accentColor,
                    backgroundColor: c.backgroundColor || d.backgroundColor,
                    textColor: c.textColor || d.textColor,
                    mutedColor: c.mutedColor || d.mutedColor,
                    giftCardBackground: c.giftCardBg || d.giftCardBackground,
                    giftCardBorder: c.giftCardBorder || d.giftCardBorder,
                    borderColor: c.giftCardBorder || d.borderColor,
                  }));
                }}
              />

              <Text as="h2" variant="headingMd">
                {t("Style")}
              </Text>
              {design.autoFitTheme && (
                <Banner tone="warning">
                  <p>
                    {t("Manual colors are saved as fallbacks but won’t apply while\n                    Auto-fit is on.")}
                  </p>
                </Banner>
              )}
              <FormLayout>
                <ColorField
                  label={t("Accent")}
                  value={design.accentColor}
                  onChange={(v) => patch('accentColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Background")}
                  value={design.backgroundColor}
                  onChange={(v) => patch('backgroundColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Title text")}
                  value={design.textColor}
                  onChange={(v) => patch('textColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Subtitle text")}
                  value={design.mutedColor}
                  onChange={(v) => patch('mutedColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Card border")}
                  value={design.borderColor}
                  onChange={(v) => patch('borderColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Gift card background")}
                  value={design.giftCardBackground}
                  onChange={(v) => patch('giftCardBackground', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Gift card border")}
                  value={design.giftCardBorder}
                  onChange={(v) => patch('giftCardBorder', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Compare-at price")}
                  value={design.comparePriceColor}
                  onChange={(v) => patch('comparePriceColor', v)}
                  disabled={design.autoFitTheme}
                />
                <RangeSlider
                  label={`Corner radius (${design.borderRadius}px)`}
                  value={design.borderRadius}
                  min={0}
                  max={28}
                  onChange={(v) =>
                    patch('borderRadius', Array.isArray(v) ? v[0] : v)
                  }
                  output
                />
              </FormLayout>

              {!embedded && (
                <Button
                  variant="primary"
                  loading={saving}
                  disabled={!dirty}
                  onClick={() => void save()}
                >
                  {t("Save widget")}
                </Button>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {t("Live preview")}
                </Text>
                <ButtonGroup variant="segmented">
                  <Button
                    pressed={previewMode === 'desktop'}
                    onClick={() => setPreviewMode('desktop')}
                  >
                    {t("Desktop")}
                  </Button>
                  <Button
                    pressed={previewMode === 'mobile'}
                    onClick={() => setPreviewMode('mobile')}
                  >
                    {t("Mobile")}
                  </Button>
                </ButtonGroup>
              </InlineStack>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Sample gifts for style only. Campaigns show the merchant’s selected\n                free gifts.")}
              </Text>
              <PromoPreview
                design={design}
                mode={previewMode}
                gifts={[
                  {
                    id: 'demo-1',
                    title: 'Reward product',
                    priceLabel: '$37.31',
                    offeredLabel: 'Free',
                  },
                ]}
              />
            </BlockStack>
          </Card>
        </div>
      </BlockStack>
    </Page>
  );
}
